import { Button, Input } from "@/shared";
import type { TOrgInfo } from "@/types";
import { useUpdateAccountPropertiesMutation } from "@api/accounts";
import { Calendar } from "@components/ui/calendar";
import Popover from "@components/ui/popover";
import Switch from "@components/ui/switch";
import { useAccountStoreShallow } from "@features/account-management";
import { cn } from "@lib/cn";
import { useAppDispatch, useAppSelector } from "@store";
import { getIsStorydocDemoAccount } from "@store/account/selectors";
import { setDialogId } from "@store/global-slice";
import { IconCalendar } from "@tabler/icons-react";
import moment from "moment";
import { useState } from "react";
import { toast } from "react-toastify";
import { useRegularDefaultState } from "../hooks";
import { useCurrentAccount } from "../providers/current-account-provider";
import type { FormHandlers, RegularSettingsType } from "../types";
import { Integrations } from "./integrations";

const toggles: { title: string; key: keyof RegularSettingsType }[] = [
  { title: "Remove Storydoc branding", key: "hidePoweredByStorydoc" },
  { title: "Enable import PDF", key: "importPdf" },
  { title: "Analytics placeholder data", key: "showAnalyticsPlaceholder" },
  { title: "Auto-GIF generate", key: "autoGenerateGif" },
  { title: "Enable custom subdomain", key: "customSubdomainEnabled" },
  { title: "Enable white label domain", key: "whitelabelDomainEnabled" },
  {
    title: "Enable sending domain ($100/month add-on, Team)",
    key: "sendingDomainEnabled",
  },
  { title: "Optimize video (admin only)", key: "optimizeVideoEnabled" },
  { title: "Default downloadable PDF", key: "downloadablePdf" },
  { title: "Disable AI image feature", key: "isAiImageFeatureDisabled" },
  { title: "Enable scroll by slide", key: "isScrollBySlideEnabled" },
  {
    title: "Allow Creator Lite users to edit other creators' documents",
    key: "isCreatorLiteCrossEditEnabled",
  },
  { title: "Enable import PDF v2", key: "importPdfV2" },
  { title: "Enable onboarding", key: "onboardingEnabled" },
];

export const RegularSettings = () => {
  const dispatch = useAppDispatch();
  const { isDifferentFromCurrentAccount } = useCurrentAccount();
  const { register, setValue, watch, handleSubmit, isLoading, account } =
    useRegularDefaultState();
  const [updateAccountProperties, { isLoading: updateAccountLoading }] =
    useUpdateAccountPropertiesMutation();

  const isStorydocDemoAccount = useAppSelector(getIsStorydocDemoAccount);
  const isTeamPlan = useAccountStoreShallow((s) => s.planType === "team");

  const onSubmit = async (data: RegularSettingsType) => {
    try {
      const payload = Object.entries(data).reduce(
        (acc: Record<string, unknown>, [key, value]) => {
          const accountValue = account?.[key as keyof TOrgInfo];

          if (
            value !== accountValue ||
            (key === "emailDomain" && !value && accountValue)
          ) {
            acc[key] = key === "emailDomain" && !value ? null : value;
          }

          if (
            key === "allowedIntegrations" &&
            value?.toString()?.trim() === accountValue?.toString()?.trim()
          ) {
            delete acc[key];
          }

          return acc;
        },
        {},
      );
      if (Object.keys(payload).length === 0)
        return toast.info("No changes were made");
      await updateAccountProperties({
        type: "account",
        payload,
        id: account?._id as string,
      }).unwrap();
      if (isDifferentFromCurrentAccount) {
        toast.success(`${account?.title} account updated successfully`);
      } else {
        toast.success("Account updated successfully");
        window.location.reload();
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to update account");
    }
    dispatch(setDialogId({ id: null }));
  };

  return (
    <div>
      <div className="tw-grid tw-grid-cols-2 tw-gap-x-20 tw-gap-y-6">
        {toggles.map((toggle) => (
          <SingleToggle
            key={toggle.key}
            label={toggle.title}
            property={toggle.key}
            register={register}
            setValue={setValue}
            watch={watch}
          />
        ))}
      </div>
      <hr className="tw-my-6 tw-border-t tw-border-blue-100" />
      <SingleInput
        label="Organization name"
        placeholder="Enter organization name"
        property="title"
        className="tw-mb-4"
        register={register}
        required={true}
        setValue={setValue}
        watch={watch}
        isLoading={isLoading}
      />
      <SingleInput
        label="API Credits Limit"
        placeholder="Enter API Credits Limit"
        property="apiCreditsLimit"
        register={register}
        setValue={setValue}
        watch={watch}
        isLoading={isLoading}
        className="tw-mb-4"
        type="number"
        min={0}
        max={Number.MAX_SAFE_INTEGER}
        validate={(value) => {
          if (value === "" || value === undefined || value === null)
            return true;
          const num = Number(value);
          return !isNaN(num) && num >= 0;
        }}
      />
      <SingleInput
        // Send flow: replaces the plan's email sends (Trial/Starter 10, Pro 50 a
        // month, Team 50 a day) with this many a month per person. Empty = plan's.
        label="Email Sends Limit (per person, monthly)"
        placeholder="Plan default"
        property="emailSendsLimit"
        register={register}
        setValue={setValue}
        watch={watch}
        isLoading={isLoading}
        className="tw-mb-4"
        type="number"
        min={0}
        max={Number.MAX_SAFE_INTEGER}
        validate={(value) => {
          if (value === "" || value === undefined || value === null)
            return true;
          const num = Number(value);
          return !isNaN(num) && num >= 0;
        }}
      />
      {!isStorydocDemoAccount && (
        <SingleInput
          label="Domain name"
          placeholder="Enter organization domain name"
          property="emailDomain"
          register={register}
          setValue={setValue}
          watch={watch}
          isLoading={isLoading}
        />
      )}
      <Integrations
        property="allowedIntegrations"
        register={register}
        setValue={setValue}
        watch={watch}
      />
      {account?.trialEnd && (
        <div className="tw-mt-4">
          <SingleInput
            label="Trial end"
            placeholder="Enter trial end"
            property="trialEnd"
            register={register}
            setValue={setValue}
            watch={watch}
            isLoading={isLoading}
          />
        </div>
      )}
      {isTeamPlan && <CancelDateInput setValue={setValue} watch={watch} />}
      <Button
        size="large"
        variant="secondary"
        title="Update account"
        className="tw-mt-6"
        loading={updateAccountLoading}
        onClick={() => handleSubmit(onSubmit)()}
      />
    </div>
  );
};

const SingleToggle = ({
  label,
  property,
  register,
  setValue,
  watch,
}: {
  label: string;
} & FormHandlers) => {
  const current = watch(property);
  return (
    <span
      className="tw-flex tw-items-center tw-justify-between tw-font-sans tw-text-[13px]/normal tw-font-semibold tw-text-blue-base"
      {...register(property)}
    >
      {label}
      <Switch>
        <Switch.Toggle
          checked={!!current}
          onCheckedChange={(value) => setValue(property, value)}
        />
      </Switch>
    </span>
  );
};

const CancelDateInput = ({
  setValue,
  watch,
}: Pick<FormHandlers, "setValue" | "watch">) => {
  const [isOpen, setIsOpen] = useState(false);
  const current = watch("cancelAt");
  const selected = current ? new Date(current) : undefined;

  const handleSelect = (date: Date | undefined) => {
    setValue("cancelAt", date ?? null);
    setIsOpen(false);
  };

  return (
    <div className="tw-mt-4 tw-w-full">
      <span className="tw-mb-1 tw-block tw-font-sans tw-text-[13px]/normal tw-font-semibold tw-text-blue-base">
        Cancellation date
      </span>
      <div className="tw-flex tw-items-center tw-gap-3">
        <Popover
          open={isOpen}
          onOpenChange={setIsOpen}
          placement="bottom-start"
        >
          <Popover.Trigger asChild>
            <button
              type="button"
              className={cn(
                "tw-flex tw-h-9 tw-w-full tw-cursor-pointer tw-items-center tw-justify-between tw-gap-2 tw-rounded tw-border tw-border-blue-100 tw-px-2 tw-font-sans tw-text-[13px]/normal tw-text-blue tw-outline-none tw-transition-colors hover:tw-bg-blue-50",
                { "tw-bg-blue-50": isOpen },
              )}
            >
              <span
                className={cn("tw-flex-1 tw-truncate tw-text-left", {
                  "tw-text-blue-300": !selected,
                })}
              >
                {selected
                  ? moment(selected).format("MMM D, YYYY")
                  : "Select cancellation date"}
              </span>
              <IconCalendar className="tw-size-4 tw-flex-shrink-0 tw-text-blue-400" />
            </button>
          </Popover.Trigger>
          <Popover.Content className="tw-z-[99999] tw-pl-1">
            <Calendar
              mode="single"
              defaultMonth={selected || new Date()}
              selected={selected}
              numberOfMonths={1}
              onSelect={handleSelect}
            />
          </Popover.Content>
        </Popover>
        {selected && (
          <button
            type="button"
            aria-label="Clear cancellation date"
            className="tw-flex-shrink-0 tw-font-sans tw-text-[13px]/normal tw-font-semibold tw-text-blue-base tw-underline"
            onClick={() => setValue("cancelAt", null)}
          >
            Clear
          </button>
        )}
      </div>
    </div>
  );
};

const SingleInput = ({
  label,
  placeholder,
  property,
  className,
  register,
  setValue,
  watch,
  required,
  isLoading,
  type = "text",
  validate,
  min,
  max,
}: {
  label: string;
  placeholder: string;
  isLoading: boolean;
  required?: boolean;
  className?: string;
  type?: "text" | "number";
  validate?: (value: RegularSettingsType[keyof RegularSettingsType]) => boolean;
  min?: number;
  max?: number;
} & FormHandlers) => {
  const current = watch(property);
  const currentValue = String(current ?? "");

  const isValidValue = () => {
    if (required && !isLoading && !current) return false;
    if (validate && current !== undefined && current !== "") {
      return validate(current);
    }
    return true;
  };

  return (
    <div
      className="tw-w-full"
      {...register(property, {
        required: !!required,
        validate: validate as any,
      })}
    >
      <span className="tw-mb-1 tw-font-sans tw-text-[13px]/normal tw-font-semibold tw-text-blue-base">
        {label}
      </span>
      <Input
        value={currentValue}
        emitValue={(value) => {
          if (type === "number") {
            const numValue = value === "" ? undefined : Number(value);
            setValue(property, numValue as any);
          } else {
            setValue(property, value as any);
          }
        }}
        isValid={isValidValue()}
        options={
          type === "number"
            ? {
                placeholder: placeholder,
                type: "number",
                min: min ?? 0,
                max: max ?? Number.MAX_SAFE_INTEGER,
                styles: className || "",
              }
            : {
                placeholder: placeholder,
                type: "text",
                styles: className || "",
              }
        }
      />
    </div>
  );
};
